// Libs
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Dropdown, DropdownMenu, DropdownToggle, ListGroup, ListGroupItem, Spinner } from "reactstrap";
import { debounce, map, difference, reduce } from "lodash";
import classnames from "classnames";

// Components, Views, Screens
import TableSearch from "../../../../base/components/Table/tableSearch";
import Checkbox from "../../../../base/components/Checkbox";

// Hooks, Utils, Helpers
import { toggleSelectItem } from "../../../helpers/toggleSelectItem";
import { useHighlight } from "../../../hooks/useHighlight";
import { fetchDataWithChunks } from "../../../helpers/api";
import { useService } from "../../../hooks/useService";
import { useLoading } from "../../../hooks/useLoading";
import { MAX_LIMIT } from "../../../constants/api";
import SegmentsService from "../../../../services/SegmentsService";

// Styles, assets
import classes from "./UserSegmentsDropdown.module.scss";


const DEFAULT_LIMIT = 15;

const MIN_SEARCH_LENGTH = 3;

export const DefaultMultiSelectValueItemView = ({ value, labels }) => {
    return value.map(id => labels[id]).join(', ');
};

export const UserSegmentsDropdown = ({
                                         onChange,
                                         value = [],
                                         placeholder,
                                         label,
                                         withoutSelectAllOption = false,
                                         valueItemViewComponent,
                                         error
                                     }) => {
    /**
     * @type {SegmentsService}
     */
    const segmentsService = useService(SegmentsService);

    const [isOpen, updateIsOpen] = useState(false);
    const [userSegments, setUserSegments] = useState([]);
    const [search, setSearch] = useState("");
    const [prevSearchRequest, setPrevSearchRequest] = useState("");
    const [page, setPage] = useState(1);
    const [hasNextPage, setHasNextPage] = useState(true);
    const [totalCount, setTotalCount] = useState(0);
    const [isLoading, { registerPromise }] = useLoading(true);
    const lastElementRef = useRef(null);


    const valueIds = map(value, 'id');
    const ingredientGroupIds = map(userSegments, 'id');
    const valueLabelsById = useMemo(() => reduce(value, (obj, item) => {
        obj[item.id] = item.name;
        return obj;
    }, {}), [value]);

    const showPlaceholder = !value?.length;

    const searchRequest = useMemo(() => {
            let newSearch = search && search.toString()?.trim()?.length >= MIN_SEARCH_LENGTH ? search : '';

            if (newSearch !== prevSearchRequest) {
                setUserSegments([]);
                setHasNextPage(true);
                setPage(1);
            }

            setPrevSearchRequest(newSearch);

            return newSearch;
        },
        [search]);

    const handleChangeSearch = (search) => {
        setSearch(search);
    };

    const { decorateText } = useHighlight(searchRequest);

    const mapSegmentToDropdown = (data = []) => {
        return data.map((item) => ({ id: item.id, name: item.fullName }));
    };

    const isSelectedAll = useMemo(() => {
        return !(difference(ingredientGroupIds, valueIds).length);
    }, [valueIds, ingredientGroupIds]);

    const selectAllItems = (newItems) => {
        const currentIds = map(newItems, 'id');

        if (isSelectedAll) {
            onChange(value.filter(({ id }) => !currentIds.includes(id)));
        } else {
            const unselectedItems = newItems.filter(({ id }) => {
                return !valueIds.includes(id);
            });

            onChange([...value, ...unselectedItems]);
        }
    };

    const handleSelectAllValues = () => {
        const loadCallback = (limit, page) => {
            const nextOffset = (page - 1) * limit;

            const params = { limit, offset: nextOffset, query: searchRequest };

            return segmentsService.getSegments(params);
        };

        if (!hasNextPage) {
            selectAllItems(userSegments);
        } else {
            registerPromise(fetchDataWithChunks({
                totalCount,
                limit: MAX_LIMIT,
                fetchCallback: loadCallback,
                resultCallback: result => result.data
            })).then((data) => {
                const newItems = mapSegmentToDropdown(data);
                setUserSegments(newItems);
                setHasNextPage(false);

                selectAllItems(newItems);
            });
        }
    };

    const handleClear = () => {
        onChange([]);
    };

    const setNextPage = () => {
        if (isLoading || !hasNextPage) return;
        setPage(page + 1);
    };

    const debounceSetNextPage = debounce(setNextPage, 300);

    const loadSegments = useCallback(() => {

        const nextOffset = (page - 1) * DEFAULT_LIMIT;

        const params = { limit: DEFAULT_LIMIT, offset: nextOffset, query: searchRequest };

        registerPromise(segmentsService.getSegments(params))
            .then((result => {
                setUserSegments([...userSegments, ...mapSegmentToDropdown(result.data)]);
                setHasNextPage(result?.pagination?.nextOffset < result?.pagination?.totalCount);
                setTotalCount(result?.pagination?.totalCount);
            }));
    }, [page, searchRequest, userSegments]);

    useEffect(() => {
        if (!lastElementRef.current) return;

        const observer = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting) {
                debounceSetNextPage();
            }
        });

        observer.observe(lastElementRef.current);

        return () => {
            observer.disconnect();
        };
    }, [debounceSetNextPage, lastElementRef.current]);

    useEffect(() => {
        loadSegments();
    }, [page, searchRequest]);

    const handleDeleteItem = (id) => {
        onChange(value.filter(item => item.id !== id));
    };

    const ValueComponent = valueItemViewComponent || DefaultMultiSelectValueItemView;

    return (<>
            {!!label && <label>{label}</label>}
            <section className="d-flex align-items-center w-100">
                <Dropdown
                    isOpen={isOpen}
                    toggle={() => updateIsOpen(prevState => !prevState)}
                    className="d-inline-block filter-dropdown cursor-pointer result-filter w-100"
                    direction="down"
                >
                    <DropdownToggle
                        className={classnames(
                            'filter-toggle max-w-100 pe-0',
                            { 'with-border': isOpen, "is-invalid select-invalid": !!error })}
                        tag="section"
                    >
                    <span
                        className={classnames(
                            'ms-2 me-1 user-select-none text-truncate',
                            { 'text-secondary': showPlaceholder}
                        )}>
                        {
                            showPlaceholder
                                ? placeholder || 'Select user segments'
                                : <ValueComponent
                                    value={valueIds}
                                    onDeleteItem={handleDeleteItem}
                                    labels={valueLabelsById}
                                />
                        }
                            </span>
                        <i className={classnames('mdi mdi-chevron-down pointer-events-none user-select-none me-2', { 'mdi-rotate-180': isOpen })}/>
                    </DropdownToggle>

                    <DropdownMenu className="filter-menu pb-1 px-1 w-100 top-50" flip={false}>
                        <TableSearch onSearch={handleChangeSearch} search={search} className="my-2"/>

                        <section>
                            <ListGroup>
                                <ListGroupItem
                                    className={classnames("d-flex bg-transparent justify-content-between", classes.ServiceItem)}>
                                    <div>
                                        {!withoutSelectAllOption && <Checkbox
                                            name="selectAllValues"
                                            text="Select all"
                                            value={isSelectedAll}
                                            onChange={handleSelectAllValues}
                                            disabled={isLoading}
                                        />}
                                    </div>
                                    <button type="button"
                                            className="text-danger border-0 bg-transparent"
                                            disabled={isLoading}
                                            onClick={handleClear}>
                                        Clear
                                    </button>
                                </ListGroupItem>

                                <div className={classnames(classes.ItemsWrapper, "custom-scrollbar")}>
                                    {userSegments.map((item, index) => (
                                        <ListGroupItem className="bg-transparent border-0" key={item.id}>
                                            <Checkbox
                                                name="ingredientGroup"
                                                text={<div {...decorateText(item.name)} />}
                                                value={valueIds.includes(item.id)}
                                                onChange={() => toggleSelectItem(item, value, onChange)}
                                            />
                                            {
                                                index === userSegments.length - 1
                                                    ? <div
                                                        ref={lastElementRef}
                                                        className="d-flex justify-content-center">
                                                    </div>
                                                    : null
                                            }
                                        </ListGroupItem>
                                    ))}
                                    {isLoading && <div className="d-flex my-2 justify-content-center">
                                        <Spinner size="sm" color="primary"/>
                                    </div>}
                                </div>
                            </ListGroup>
                        </section>
                    </DropdownMenu>
                </Dropdown>
            </section>
            {error &&
                <span className="invalid-feedback d-block">{error}</span>
            }
        </>
    );
};

